import { useEffect, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent, ReactElement } from "react";
import type { KundeAkte as KundeAkteDaten, Unternehmen, Zuordnung } from "@kundeninfo/api-client";
import { markenTextfarben } from "../markenfarben.js";
import { relativeZeit, formatDatum } from "../format.js";
import "./KundenAkte.css";

export type AktenTabId =
  | "uebersicht" | "stammdaten" | "ansprechpartner" | "angebote"
  | "auftraege" | "wichtiges" | "notizen" | "dateien";

/** Die Tab-Leiste ist die beständige Navigationsebene innerhalb eines Kunden.
 *  Sie steht vollständig da, auch wo der Inhalt erst in einem späteren Paket kommt. */
export const AKTEN_TABS: { id: AktenTabId; label: string; paket: number }[] = [
  { id: "uebersicht",      label: "Übersicht",       paket: 1 },
  { id: "stammdaten",      label: "Stammdaten",      paket: 1 },
  { id: "ansprechpartner", label: "Ansprechpartner", paket: 2 },
  { id: "angebote",        label: "Angebote",        paket: 3 },
  { id: "auftraege",       label: "Aufträge",        paket: 3 },
  { id: "wichtiges",       label: "Wichtiges",       paket: 3 },
  { id: "notizen",         label: "Notizen",         paket: 4 },
  { id: "dateien",         label: "Dateien",         paket: 4 },
];

export type KundenAkteProps = {
  kunde: KundeAkteDaten;
  activeTab: AktenTabId;
  onTab: (id: AktenTabId) => void;
  /** Für die Farbe der Kürzel-Chips im Kopf. Ohne Angabe erscheinen sie neutral. */
  companies?: Unternehmen[];
};

function chipStil(colorHex: string | undefined): CSSProperties | undefined {
  if (colorHex === undefined) return undefined;
  const { hell, dunkel } = markenTextfarben(colorHex);
  return {
    "--ka-chip-bg": `color-mix(in srgb, ${colorHex} 14%, var(--ki-surface))`,
    "--ka-chip-rand": `color-mix(in srgb, ${colorHex} 45%, transparent)`,
    "--ka-chip-text-hell": hell,
    "--ka-chip-text-dunkel": dunkel,
  } as CSSProperties;
}

function KundenChip({ r, colorHex }: { r: Zuordnung; colorHex: string | undefined }): ReactElement {
  return (
    <span className="ka-chip" style={chipStil(colorHex)}>
      <span className="ka-chip__code">{r.shortCode}</span>
      <span className="ka-chip__sep"> · </span>
      <span className="ka-chip__nr">{r.customerNumber}</span>
    </span>
  );
}

function UebersichtInhalt({ kunde }: { kunde: KundeAkteDaten }): ReactElement {
  return (
    <div>
      <p className="ka-uebersicht__zeile">
        {kunde.industry ?? "Branche unbekannt"}
        {kunde.city !== null && kunde.city !== undefined && kunde.city !== "" ? ` · ${kunde.city}` : ""}
      </p>
      <p className="ka-uebersicht__zeile">Letzte Aktivität: {relativeZeit(kunde.lastActivityAt)}</p>
      <p className="ka-uebersicht__zeile">
        {kunde.relations.length === 0
          ? "Noch keinem Unternehmen zugeordnet."
          : `Zugeordnet bei ${kunde.relations.length} Unternehmen: ` +
            kunde.relations.map((r) => `${r.companyName} (${r.status})`).join(", ")}
      </p>
    </div>
  );
}

function StammdatenInhalt({ kunde }: { kunde: KundeAkteDaten }): ReactElement {
  return (
    <dl className="ka-feldliste">
      <dt>Straße</dt><dd>{kunde.street ?? "—"}</dd>
      <dt>PLZ / Ort</dt><dd>{[kunde.postalCode, kunde.city].filter(Boolean).join(" ") || "—"}</dd>
      <dt>Telefon</dt><dd>{kunde.phone ?? "—"}</dd>
      <dt>E-Mail</dt><dd>{kunde.email ?? "—"}</dd>
      <dt>USt-IdNr.</dt><dd>{kunde.vatId ?? "—"}</dd>
      <dt>Branche</dt><dd>{kunde.industry ?? "—"}</dd>
    </dl>
  );
}

/** Rundet den Index innerhalb von AKTEN_TABS um, statt am Rand stehen zu bleiben. */
function ringIndex(index: number): number {
  return (index + AKTEN_TABS.length) % AKTEN_TABS.length;
}

export function KundenAkte(p: KundenAkteProps): ReactElement {
  const companies = p.companies ?? [];
  // Nicht-null-Behauptung: AKTEN_TABS ist ein feststehendes, nie leeres Literal — [0]
  // existiert immer, auch wenn noUncheckedIndexedAccess das dem Indexzugriff nicht ansieht.
  const aktiv = AKTEN_TABS.find((t) => t.id === p.activeTab) ?? AKTEN_TABS[0]!;

  const tabsRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Partial<Record<AktenTabId, HTMLButtonElement>>>({});
  const [ueberlaufLinks, setUeberlaufLinks] = useState(false);
  const [ueberlaufRechts, setUeberlaufRechts] = useState(false);

  // Zeigt einen weichen Verlauf an genau der Seite, an der tatsächlich noch Tabs verdeckt
  // sind — nicht pauschal, denn bei genug Platz (Desktop) läuft nichts über.
  useEffect(() => {
    const leiste = tabsRef.current;
    if (leiste === null) return;
    const messen = (): void => {
      setUeberlaufLinks(leiste.scrollLeft > 0);
      setUeberlaufRechts(leiste.scrollLeft + leiste.clientWidth < leiste.scrollWidth - 1);
    };
    messen();
    leiste.addEventListener("scroll", messen);
    window.addEventListener("resize", messen);
    return () => {
      leiste.removeEventListener("scroll", messen);
      window.removeEventListener("resize", messen);
    };
  }, []);

  // Der aktive Tab muss immer sichtbar sein, auch wenn er außerhalb des sichtbaren
  // Ausschnitts liegt (z. B. nach einem Wechsel per Pfeiltaste über den Rand hinaus).
  useEffect(() => {
    const knopf = tabRefs.current[aktiv.id];
    if (knopf?.scrollIntoView !== undefined) {
      knopf.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
  }, [aktiv.id]);

  // Bekanntes Muster für Tableisten: nur der aktive Tab liegt in der Tabulatorreihenfolge,
  // gewechselt wird mit den Pfeiltasten (automatische Aktivierung, kein zusätzliches Enter
  // nötig). Home/End springen an den Anfang/das Ende der Leiste.
  function beiTaste(e: KeyboardEvent<HTMLDivElement>): void {
    const index = AKTEN_TABS.findIndex((t) => t.id === aktiv.id);
    let ziel: number | null = null;
    if (e.key === "ArrowRight") ziel = ringIndex(index + 1);
    else if (e.key === "ArrowLeft") ziel = ringIndex(index - 1);
    else if (e.key === "Home") ziel = 0;
    else if (e.key === "End") ziel = AKTEN_TABS.length - 1;
    if (ziel === null) return;
    e.preventDefault();
    const zielTab = AKTEN_TABS[ziel]!;
    p.onTab(zielTab.id);
    tabRefs.current[zielTab.id]?.focus();
  }

  return (
    <div className="ka">
      <header className="ka__kopf">
        <h1 className="ka__titel">{p.kunde.companyName}</h1>
        <div className="ka__chips">
          {p.kunde.relations.map((r) => (
            <KundenChip
              key={r.shortCode}
              r={r}
              colorHex={companies.find((c) => c.shortCode === r.shortCode)?.colorHex}
            />
          ))}
        </div>
        <div className="ka__tabs-wrap">
          <div
            ref={tabsRef}
            role="tablist"
            aria-label="Bereiche der Kundenakte"
            className="ka__tabs"
            onKeyDown={beiTaste}
          >
            {AKTEN_TABS.map((t) => (
              <button
                key={t.id}
                ref={(el) => { if (el !== null) tabRefs.current[t.id] = el; }}
                type="button"
                role="tab"
                aria-selected={t.id === aktiv.id}
                tabIndex={t.id === aktiv.id ? 0 : -1}
                className={`ka__tab${t.id === aktiv.id ? " ka__tab--aktiv" : ""}`}
                onClick={() => p.onTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <span className="ka__tabs-fade ka__tabs-fade--links" aria-hidden="true" hidden={!ueberlaufLinks} />
          <span className="ka__tabs-fade ka__tabs-fade--rechts" aria-hidden="true" hidden={!ueberlaufRechts} />
        </div>
      </header>

      {/* Der Tab-Wechsel tauscht ausschließlich diesen einen Bereich — Kopf und Tab-Leiste
          bleiben stehen. "Der Kunde ist eine Ebene, kein Ziel." */}
      <div role="tabpanel" className="ka__panel" aria-label={aktiv.label}>
        {aktiv.paket > 1
          ? <p className="ka__platzhalter">Dieser Bereich kommt mit Paket {aktiv.paket}.</p>
          : aktiv.id === "stammdaten"
            ? <StammdatenInhalt kunde={p.kunde} />
            : <UebersichtInhalt kunde={p.kunde} />}
      </div>
    </div>
  );
}
